import React, { memo, useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { Button, Form, Input, Modal, Select, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { entityList } from '../entity-list'
import { AddModalProps } from '../interfaces'
import { UploadFileStatus, UploadFile } from 'antd/es/upload/interface'
import TemplateEditor, { IVariableGroup } from '../../message-templates-editor'

const AddModal: React.FC<AddModalProps> = memo((props: AddModalProps) => {
  const { officesList, onOk, onCancel, record, modalIsOpen } = props
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [isOkDisabled, setIsOkDisabled] = useState(true)
  const [templateType, setTemplateType] = useState(record?.type || 'doc')
  const [selectedEntity, setSelectedEntity] = useState<string | undefined>(record?.entity)
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isInitializingRef = useRef(false)
  const formInitializedRef = useRef(false)
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const recordValues = useMemo(() => {
    if (!record) return null
    return {
      id: record.id,
      type: record.type,
      office_id: record.office_id,
      name: record.name,
      entity: record.entity,
      description: record.description,
      message: record.message,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    record?.id,
    record?.type,
    record?.office_id,
    record?.name,
    record?.entity,
    record?.description,
    record?.message,
  ])

  useEffect(() => {
    if (modalIsOpen) {
      isInitializingRef.current = true
      formInitializedRef.current = true

      const values = recordValues
        ? {
            type: recordValues.type || 'doc',
            office_id: recordValues.office_id,
            name: recordValues.name || '',
            entity: recordValues.entity,
            description: recordValues.description || '',
            message: recordValues.message || undefined,
          }
        : {
            type: 'doc',
            office_id: undefined,
            name: '',
            entity: undefined,
            description: '',
            message: undefined,
          }

      form.resetFields()
      form.setFieldsValue(values)
      setTemplateType(recordValues?.type || 'doc')
      setSelectedEntity(recordValues?.entity)

      if (recordValues && recordValues.type !== 'message' && record?.file_name) {
        setFileList([
          {
            uid: `file_${record.id}`,
            name: record.file_name,
            status: 'done' as UploadFileStatus,
            url: `/api/template/${record.id}`,
          },
        ])
      } else {
        setFileList([])
      }

      requestAnimationFrame(async () => {
        try {
          await form.validateFields()
          setIsOkDisabled(false)
        } catch (error) {
          setIsOkDisabled(true)
        } finally {
          setTimeout(() => {
            isInitializingRef.current = false
          }, 100)
        }
      })
    } else {
      if (formInitializedRef.current) {
        form.resetFields()
      }
      setTemplateType('doc')
      setSelectedEntity(undefined)
      setFileList([])
      setIsOkDisabled(true)
      formInitializedRef.current = false
      isInitializingRef.current = false

      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current)
        validationTimeoutRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsOpen, recordValues])

  const handleOk = useCallback(async () => {
    try {
      await form.validateFields()
      const values = form.getFieldsValue()
      setLoading(true)
      const formData = new FormData()

      if (['doc', 'xls', 'pdf'].includes(templateType)) {
        const file = values?.file as File | undefined
        if (file instanceof File) {
          formData.append('file', file)
        } else {
          console.warn('File is not a File instance:', file, typeof file)
        }
      }

      formData.append('name', values?.name)
      formData.append('entity', values?.entity)
      formData.append('type', values?.type)

      if (values?.description) {
        formData.append('description', values.description)
      }

      if (values?.office_id?.length) {
        formData.append(
          'office_id',
          Array.isArray(values.office_id) ? values.office_id.join(',') : values.office_id,
        )
      }

      if (templateType === 'message' && values?.message) {
        formData.append('message', values.message)
      }

      try {
        const url = record?.id ? `/api/template/${record.id}` : '/api/add-template'
        const rawResponse = await fetch(url, {
          method: 'POST',
          body: formData,
        })
        const response = await rawResponse.json()
        onOk?.(response)
      } catch (error) {
        console.warn(error)
        return { error }
      } finally {
        setLoading(false)
      }
    } catch (errInfo) {
      console.error('Validate Failed:', errInfo)
    }
  }, [form, templateType, record, onOk])

  const typeOptions = useMemo(
    () => [
      { value: 'doc', label: 'Документ DOC' },
      { value: 'xls', label: 'Документ XLS' },
      { value: 'pdf', label: 'Документ PDF' },
      { value: 'message', label: 'Сообщение' },
    ],
    [],
  )

  const entityOptionsForType = useMemo(() => {
    return Object.keys(entityList).map(key => {
      const params = entityList[key]
      return {
        value: key,
        label: params?.label,
        disabled: params?.disabled,
      }
    })
  }, [])

  const variablesByEntity: Record<string, IVariableGroup[]> = useMemo(
    () => ({
      hr_page: [
        {
          group: 'Кандидат',
          items: [
            { key: 'candidate_name', label: 'ФИО' },
            { key: 'candidate_phone', label: 'Телефон' },
            { key: 'candidate_age', label: 'Возраст' },
            { key: 'candidate_gender', label: 'Пол' },
          ],
        },
        {
          group: 'Обращение',
          items: [
            { key: 'vacancy', label: 'Вакансия' },
            { key: 'resource_name', label: 'Ресурс' },
            { key: 'status', label: 'Статус обращения' },
            { key: 'interview_type', label: 'Тип собеседования' },
            { key: 'interview_datetime', label: 'Дата и время собеседования' },
            { key: 'date_interview', label: 'Дата собеседования' },
            { key: 'date_interview_time', label: 'Время собеседования' },
            { key: 'came_request', label: 'Пришел на собеседование' },
            { key: 'date_callback', label: 'Дата повторного звонка' },
            { key: 'date_repeated_call', label: 'Повторный звонок (не явившемуся)' },
            { key: 'rejection', label: 'Причина отказа' },
            { key: 'rejection_candidate', label: 'Причина отказа явки кандидата' },
            { key: 'employment_date', label: 'Планируемая дата выхода на работу' },
            { key: 'work_comment', label: 'Результат о выходе на работу' },
            { key: 'date_probationary', label: 'Дата прохождения испытательного срока' },
            { key: 'result_trial_period', label: 'Результат испытательного срока' },
            { key: 'result_trial', label: 'Причина не прохождения испытательного срока' },
            { key: 'comment', label: 'Примечания' },
          ],
        },
        {
          group: 'Менеджер',
          items: [
            { key: 'manager_name', label: 'ФИО менеджера' },
            { key: 'manager_phone', label: 'Телефон менеджера' },
          ],
        },
      ],
      order_page: [
        {
          group: 'Покупатель',
          items: [
            { key: 'customer_name', label: 'ФИО / Наим. юр. лица' },
            { key: 'customer_phone', label: 'Телефон' },
            { key: 'customer_email', label: 'Email' },
            { key: 'customer_passport_number', label: 'Серия и номер паспорта' },
            { key: 'customer_passport_issued', label: 'Кем выдан паспорт' },
            { key: 'customer_birthday', label: 'Дата рождения' },
            { key: 'customer_reg_addr', label: 'Адрес регистрации' },
          ],
        },
        {
          group: 'Юр. данные покупателя',
          items: [
            { key: 'customer_director', label: 'Директор' },
            { key: 'customer_coop_address', label: 'Юр. адрес' },
            { key: 'customer_inn', label: 'ИНН' },
            { key: 'customer_invoice_curr', label: 'Расчётный счёт' },
            { key: 'customer_invoice_corr', label: 'Кор. счёт' },
            { key: 'customer_bank_name', label: 'Банк' },
            { key: 'customer_bik', label: 'БИК' },
            { key: 'customer_okpo', label: 'ОКПО' },
            { key: 'customer_kpp', label: 'КПП' },
            { key: 'customer_ogrn', label: 'ОГРН' },
          ],
        },
        {
          group: 'Заказ',
          items: [
            { key: 'current_date', label: 'Текущая дата' },
            { key: 'order_date', label: 'Дата создания заказа' },
            { key: 'order_contract_number', label: 'Номер договора' },
            { key: 'contract_number', label: 'Номер тех. задания' },
            { key: 'object_address', label: 'Адрес объекта' },
            { key: 'contract_deadline', label: 'Срок исполнения договора' },
            { key: 'order_attribute', label: 'Признак заказа' },
          ],
        },
        {
          group: 'Менеджер',
          items: [
            { key: 'manager_name', label: 'ФИО менеджера' },
            { key: 'manager_phone', label: 'Телефон менеджера' },
          ],
        },
        {
          group: 'Офис / Продавец',
          items: [
            { key: 'office', label: 'Офис' },
            { key: 'saler_name', label: 'Наименование продавца' },
            { key: 'office_city', label: 'Город офиса' },
            { key: 'office_phone', label: 'Телефон офиса' },
            { key: 'org_email', label: 'Email продавца' },
            { key: 'org_address', label: 'Адрес продавца' },
          ],
        },
        {
          group: 'Финансы',
          items: [
            { key: 'total_cost', label: 'Сумма' },
            { key: 'prepayment_sum', label: 'Предоплата' },
          ],
        },
      ],
      claim_page: [
        {
          group: 'Покупатель',
          items: [
            { key: 'customer_name', label: 'ФИО клиента' },
            { key: 'customer_phone', label: 'Телефон клиента' },
          ],
        },
        {
          group: 'Рекламация',
          items: [
            { key: 'created_at', label: 'Дата заявки на рекламацию' },
            { key: 'address', label: 'Адрес объекта' },
            { key: 'date_application', label: 'Дата заказа' },
            { key: 'num_tech', label: 'Номер тех. задания' },
            { key: 'claim', label: 'Суть претензии' },
            { key: 'dealer_event', label: 'Мероприятия дилера' },
            { key: 'reclamation_department_event', label: 'Мероприятия отдела рекламации' },
            { key: 'parent_id', label: 'ID заказа' },
            { key: 'production_date', label: 'Дата производства факт' },
            { key: 'delivery_date', label: 'Дата доставки факт' },
            { key: 'mount_date', label: 'Дата монтажа факт' },
            { key: 'order_attribute', label: 'Признак заказа' },
            { key: 'office', label: 'Офис' },
          ],
        },
        {
          group: 'Менеджер',
          items: [
            { key: 'manager_name', label: 'ФИО менеджера' },
            { key: 'manager_phone', label: 'Телефон менеджера' },
          ],
        },
      ],
    }),
    [],
  )

  const activeVariables = useMemo(
    () => (selectedEntity ? variablesByEntity[selectedEntity] ?? [] : []),
    [selectedEntity, variablesByEntity],
  )

  const officeOptions = useMemo(
    () => (selectedEntity === 'hr_page' ? officesList?.hr || [] : officesList?.sale || []),
    [selectedEntity, officesList],
  )
  const formItemStyle = { marginBottom: 16 }

  const handleTypeChange = useCallback(
    (value: string) => {
      setTemplateType(value)
      setSelectedEntity(undefined)
      form.resetFields(['office_id', 'entity', 'file', 'message'])
    },
    [form],
  )

  const handleFileChange = useCallback(
    (info: any) => {
      const { fileList: newFileList } = info
      setFileList(newFileList)

      const { file } = info
      const fileObj = file.originFileObj || (file instanceof File ? file : null)
      if (fileObj instanceof File) {
        form.setFieldsValue({
          file: fileObj,
        })
        form.validateFields(['file']).catch(() => undefined)
      }
    },
    [form],
  )

  const handleFileRemove = useCallback(() => {
    setFileList([])
    form.setFieldsValue({ file: undefined })
    form.validateFields(['file']).catch(() => undefined)
    return true
  }, [form])

  const accept = useMemo(() => {
    if (templateType === 'doc') return '.doc, .docx'
    if (templateType === 'xls') return '.xls, .xlsx'
    if (templateType === 'pdf') return '.pdf'
  }, [templateType])

  const onFieldsChange = useCallback(() => {
    if (isInitializingRef.current) {
      return
    }
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current)
    }
    validationTimeoutRef.current = setTimeout(() => {
      form
        .validateFields({ validateOnly: true })
        .then(() => setIsOkDisabled(false))
        .catch(() => setIsOkDisabled(true))
    }, 150)
  }, [form])

  return (
    <Modal
      key={record?.id || 'new'}
      title={!record?.id ? 'Загрузка нового шаблона' : 'Изменение шаблона'}
      onOk={handleOk}
      onCancel={onCancel}
      open={modalIsOpen}
      destroyOnHidden={true}
      okButtonProps={{ disabled: isOkDisabled || loading }}
    >
      {modalIsOpen && (
        <Form
          form={form}
          labelCol={{ flex: '120px' }}
          labelAlign="left"
          labelWrap
          wrapperCol={{ flex: 1 }}
          onFieldsChange={onFieldsChange}
        >
          <Form.Item
            label="Тип шаблона"
            name="type"
            style={formItemStyle}
            rules={[{ required: true, message: 'Выберите Тип шаблона!' }]}
          >
            <Select
              placeholder="Выберите тип"
              options={typeOptions}
              onChange={handleTypeChange}
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            label="Расположение"
            name="entity"
            style={formItemStyle}
            rules={[{ required: true, message: 'Выберите расположение!' }]}
          >
            <Select
              placeholder="Выберите расположение"
              options={entityOptionsForType}
              disabled={loading}
              onChange={value => {
                setSelectedEntity(value)
                form.resetFields(['office_id'])
              }}
            />
          </Form.Item>
          {selectedEntity && officeOptions?.length ? (
            <Form.Item
              label="Офис"
              name="office_id"
              style={formItemStyle}
              rules={[{ required: true, message: 'Выберите Офис!' }]}
            >
              <Select
                placeholder="Выберите Офис"
                options={officeOptions}
                disabled={loading}
                mode="multiple"
              />
            </Form.Item>
          ) : null}
          {templateType !== 'message' ? (
            <Form.Item
              label="Файл"
              name="file"
              style={formItemStyle}
              rules={!record?.id ? [{ required: true, message: 'Выберите файл!' }] : []}
            >
              <div style={{ maxWidth: '350px' }}>
                <Upload
                  name="file"
                  accept={accept}
                  beforeUpload={() => false}
                  showUploadList={true}
                  fileList={fileList}
                  disabled={loading}
                  onChange={handleFileChange}
                  onRemove={handleFileRemove}
                >
                  <Button icon={<UploadOutlined />}>Выбрать {templateType.toUpperCase()}</Button>
                </Upload>
              </div>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                key="message-editor"
                label="Сообщение"
                name="message"
                style={formItemStyle}
                rules={[{ required: true, message: 'Заполните сообщение!' }]}
              >
                <TemplateEditor variables={activeVariables} />
              </Form.Item>
            </>
          )}
          <Form.Item
            label="Название"
            name="name"
            style={formItemStyle}
            rules={[{ required: true, message: 'Укажите название!' }]}
          >
            <Input disabled={loading} />
          </Form.Item>
          <Form.Item label="Описание" name="description" style={formItemStyle}>
            <Input.TextArea autoSize={{ minRows: 3, maxRows: 5 }} disabled={loading} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
})

AddModal.displayName = 'AddModal'

export default AddModal
